import { memo, useEffect, useState } from "react";
import ReferenceEntry, { DisplayFromFHIRReference } from "./ReferenceEntry";
import { DateTimeEntry, getDateTimeDisplay } from "./PeriodEntry";
import CodeableConceptEntry, { DisplayFromFHIRCodeableConcept } from "./CodeableConceptEntry";
import ExpandToAddOrEdit from "../components/ExpandToAddOrEdit";

const DisplayFromFHIRAttester = ({ attester }) => {
  if (attester) {
      if (Array.isArray(attester) || typeof attester !== "object") {
          return <>[ERROR: Attester data not interpretable.]</>;
      }
      return <div style={{ marginLeft: "24px" }}>
          {attester.mode && <span><b>Mode: </b><DisplayFromFHIRCodeableConcept codeableConcept={attester.mode} /></span>}
          {attester.time && <p><b>Date/Time: </b>{getDateTimeDisplay(attester.time)} </p>}
          {attester.party && <div><b>Party: </b><DisplayFromFHIRReference reference={attester.party} /></div>}
      </div>
  } else {
      return <></>
  }
}

const attesterResourceTypes = ['Practitioner', 'PractitionerRole', 'Organization', 'Patient', 'RelatedPerson'];
const limitedAttesterResourceTypes = ['Practitioner', 'Organization'];
const AttesterEntry = memo(({ elementName, fieldLabel, startingValue, setResourceState, dataEntryStyle, startCollapsed }) => {
  let startingAttester = { "mode": "", "time": "", "party": "" };
  if (!startingValue) {
    startingValue = "";
  } else {
    if (startingValue.extension) { startingAttester.extension = startingValue.extension; }
    if (startingValue.mode) { startingAttester.mode = startingValue.mode; }
    if (startingValue.time) { startingAttester.time = startingValue.time; }
    if (startingValue.party) { startingAttester.party = startingValue.party; }
  }

  const [attesterState, setAttesterState] = useState(JSON.parse(JSON.stringify(startingAttester || {})));
  const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);

  useEffect((() => {
    if (Object.keys(attesterState).length) {
      let newAttester = {};
      if (attesterState.extension) { newAttester.extension = attesterState.extension; }
      if (attesterState.mode) { newAttester.mode = attesterState.mode; }
      if (attesterState.time) { newAttester.time = attesterState.time; }
      if (attesterState.party && Object.keys(attesterState.party).length > 0) {
        newAttester.party = attesterState.party;
      }
      if (Object.keys(newAttester).length === 0) {
        newAttester = null;
      }
      setResourceState(prevState => { return { ...prevState, [elementName]: newAttester } });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [attesterState]);

  if (startCollapsedState) {
    return <>
      <div>
        <b>{fieldLabel}: </b>
        {startingValue && <DisplayFromFHIRAttester attester={startingAttester} />}
        &nbsp;&nbsp;
        <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />
      </div>
    </>
  } else {
    return <>
      <p style={{ marginBottom: "2px" }}><b>{fieldLabel}: </b></p>
      <div style={{ marginLeft: "24px" }}>
        <CodeableConceptEntry elementName='mode' fieldLabel='Mode (e.g. reviewer, endorser, editor)'
          codeableConceptLevelValueSet={[
            {
              "coding": [{ system: "http://hl7.org/fhir/contributor-role", code: "author", display: "Author/Creator" }]
            },
            {
              "coding": [{ system: "http://hl7.org/fhir/contributor-role", code: "recorder", display: "Recorder" }]
            },
            {
              "coding": [{ system: "http://hl7.org/fhir/contributor-role", code: "reviewer", display: "Reviewer" }]
            },
            {
              "coding": [{ system: "http://hl7.org/fhir/contributor-role", code: "endorser", display: "Endorser" }]
            },
            {
              "coding": [{ system: "http://hl7.org/fhir/contributor-role", code: "editor", display: "Editor" }]
            },
            {
              "coding": [{ system: "http://hl7.org/fhir/contributor-role", code: "informant", display: "Informant" }]
            },
            {
              "coding": [{ system: "http://hl7.org/fhir/contributor-role", code: "funder", display: "Funder" }]
            },
            {
              "coding": [{ system: "http://hl7.org/fhir/contributor-role", code: "publisher", display: "Publisher" }]
            }
          ]}
          startingValue={attesterState.mode} setResourceState={setAttesterState} />
        <DateTimeEntry elementName='time'
          fieldLabel='Date/Time' startingValue={attesterState.time} setResourceState={setAttesterState} />
        <ReferenceEntry elementName='party' fieldLabel={attesterState.mode?.display || "Contributor"}
          dataEntryStyle={dataEntryStyle}
          referencedResourceTypes={dataEntryStyle === "humanAuthor" ? limitedAttesterResourceTypes : attesterResourceTypes}
          enableCreation={true}
          startingValue={attesterState.party} setResourceState={setAttesterState} />
      </div>
    </>
  }
})

export default AttesterEntry;
export { DisplayFromFHIRAttester };